// Session initialization: restore, scan, and tree polling.

import {
  documentId,
  setSessionId,
  setDocumentId,
  setCachedTree,
  setLastScan,
  setSuggestEdits,
  setStatus,
  setDocumentReady,
  saveToSettings,
  loadFromSettings,
  activeNodeMap,
  setLastSyncedNodeMap,
  setLastSyncedAt,
  computePendingDelta,
  pendingDeltaCount,
  updateSyncStatus,
} from "./state";
import { getCurrentUser } from "./auth";
import {
  healthCheck,
  initSessionWithScan,
  getDocumentTree,
  sendDelta,
} from "../core/apiClient";
import { scanDocument, initializeNodeMaps, applyAndDiffToActive } from "../core/documentScanner";
import { addMessage, devLog } from "./messages";
import { renderClauseTree } from "./clauseLinks";
import { loadChatHistory } from "./chat";

// Check AutoSave status and show banner if off
async function checkAutoSave() {
  try {
    let autoSaveOn = true;
    await Word.run(async (context) => {
      context.document.load("autoSaveOn");
      await context.sync();
      autoSaveOn = context.document.autoSaveOn;
    });
    const banner = document.getElementById("autosave-banner");
    if (banner) banner.style.display = autoSaveOn ? "none" : "";
  } catch {
    // autoSaveOn not available in this host version — skip silently
  }
}

// Flush pending changes to backend (diff activeNodeMap vs lastSyncedNodeMap)
export async function flushToBackend() {
  const { added, removed } = computePendingDelta();
  if (!added.length && !removed.length) {
    updateSyncStatus("synced");
    return;
  }
  updateSyncStatus("syncing");
  try {
    await sendDelta(documentId, added, removed, []);
    setLastSyncedNodeMap({ ...activeNodeMap });
    setLastSyncedAt(Date.now());
    updateSyncStatus("synced");
  } catch (err) {
    // devLog("SYNC_ERR", err.message);
    updateSyncStatus("error");
  }
}

// Restore a previous session from Office.Settings (returns true if restored)
export async function tryRestoreSession() {
  const savedDocId = loadFromSettings("miai_doc_id");
  const savedSessionId = loadFromSettings("miai_session_id");
  const savedDocName = loadFromSettings("miai_doc_name");
  const savedTree = loadFromSettings("miai_tree");

  if (!savedDocId || !savedSessionId) return false;

  // If user changed since last session, force fresh init so chats don't bleed across users
  const savedUserId = loadFromSettings("miai_user_id");
  const currentUser = getCurrentUser();
  if (savedUserId && currentUser?.id && savedUserId !== currentUser.id) {
    return false;
  }

  let currentDocId = "";
  try {
    await Word.run(async (context) => {
      const customProps = context.document.properties.customProperties;
      customProps.load("items");
      await context.sync();
      const existing = customProps.items.find((p) => p.key === "MiAI_DocID");
      if (existing) currentDocId = existing.value;
    });
  } catch {
    return false;
  }

  const expectedDocId = "doc_" + currentDocId;
  if (!currentDocId || expectedDocId !== savedDocId) return false;

  try {
    const health = await healthCheck();
    if (health.status !== "ok" && health.status !== "not_initialized") return false;
  } catch {
    return false;
  }

  try {
    const tree = await getDocumentTree(savedDocId);
    if (!tree || tree.error || !tree.structure) return false;
  } catch {
    return false;
  }

  setSessionId(savedSessionId);
  setDocumentId(savedDocId);

  const savedSuggestEdits = loadFromSettings("suggestEdits");
  if (savedSuggestEdits) {
    setSuggestEdits(true);
    document.getElementById("suggest-edits-toggle")?.classList.add("active");
  }

  if (savedTree && savedTree.length > 0) {
    setCachedTree(savedTree);
    renderClauseTree(savedTree);
  }

  setStatus("Ready", "ready");
  setDocumentReady(true);
  checkAutoSave();
  addMessage("system", `Welcome back — ${savedDocName || "your document"} is ready.`);
  loadChatHistory();

  // Detect any offline edits since last session
  try {
    const scan = await scanDocument();
    setLastScan(scan);
    applyAndDiffToActive(scan.nodes);
    const pending = pendingDeltaCount();
    updateSyncStatus(pending > 0 ? "pending" : "synced", pending);
  } catch (e) {
    // devLog("RESTORE_SCAN_ERR", e.message);
  }

  return true;
}

// Full document init: health check → scan → backend init → render tree
export async function initializeDocument() {
  try {
    setStatus("Connecting…", "loading");
    addMessage("system", "Connecting…");

    const health = await healthCheck();
    // devLog("HEALTH", health);
    // "not_initialized" is normal on cold start — the contract enterprise module
    // initializes lazily on first /session/init call, not during server startup.
    const serverUp = health.status === "ok" || health.status === "not_initialized";
    if (!serverUp) {
      setStatus("Starting up…", "loading");
      addMessage("system", "Service is starting up. Retrying shortly…");
      setTimeout(initializeDocument, 5000);
      return;
    }

    setStatus("Reading document…", "loading");

    // Get or create persistent document ID via custom property
    let docId = "";
    try {
      await Word.run(async (context) => {
        const customProps = context.document.properties.customProperties;
        customProps.load("items");
        await context.sync();

        const existing = customProps.items.find((p) => p.key === "MiAI_DocID");
        if (existing) {
          docId = existing.value.replace(/^doc_/, "").replace(/^miai_/, "");
        } else {
          const raw = crypto.randomUUID().replace(/-/g, "");
          docId = raw.slice(0, 16);
          customProps.add("MiAI_DocID", docId);
          await context.sync();
        }
      });
    } catch (e) {
      console.warn("[DOC_ID] Custom property read/write failed:", e);
    }
    // devLog("DOC_ID", docId);

    addMessage("system", "Reading document…");
    const scan = await scanDocument();
    setLastScan(scan);
    initializeNodeMaps(scan.nodes);
    // devLog("SCAN", {
    //   nodes: scan.nodes.length,
    //   styles: Object.keys(scan.styles).length,
    //   toc: scan.toc.length,
    // });

    const fullDocId = docId ? `doc_${docId}` : undefined;
    setStatus("Analysing…", "loading");
    addMessage("system", "Analysing document structure…");

    const currentUser = getCurrentUser();
    const initRes = await initSessionWithScan(scan, fullDocId, currentUser?.id);
    setSessionId(initRes.session_id);
    setDocumentId(initRes.document_id);
    // devLog("INIT_RESPONSE", initRes);

    saveToSettings("miai_doc_id", initRes.document_id);
    saveToSettings("miai_session_id", initRes.session_id);
    saveToSettings("miai_doc_name", initRes.doc_name);
    if (currentUser?.id) saveToSettings("miai_user_id", currentUser.id);

    if (initRes.top_level_clauses && initRes.top_level_clauses.length > 0) {
      setCachedTree(initRes.top_level_clauses);
      saveToSettings("miai_tree", initRes.top_level_clauses);
      renderClauseTree(initRes.top_level_clauses);
      addMessage(
        "system",
        `Ready — found ${initRes.total_clauses} clauses across ~${initRes.total_pages} pages.`
      );
      setStatus("Ready", "ready");
      setDocumentReady(true);
      updateSyncStatus("synced");
      setLastSyncedAt(Date.now());
      checkAutoSave();
      loadChatHistory();
    } else {
      addMessage("system", `${initRes.message}`);
      setStatus("Analysing…", "loading");
      pollForTree(initRes.document_id);
    }
  } catch (err) {
    setStatus("Something went wrong", "error");
    // devLog("INIT_ERR", `${err.name}: ${err.message}\n${err.stack || ""}`);
    addMessage("system", `Error: ${err.message}`);
  }
}

// Poll /document/{id}/status every 5s until tree is ready (max 10 min)
async function pollForTree(docId) {
  const POLL_INTERVAL = 5000;
  const MAX_POLLS = 120;

  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL));
    const elapsed = (((i + 1) * POLL_INTERVAL) / 1000).toFixed(0);
    try {
      const tree = await getDocumentTree(docId);
      // devLog("POLL_TREE", { structure: tree?.structure?.length });

      if (tree && tree.structure && tree.structure.length > 0) {
        addMessage(
          "system",
          `Ready — found ${tree.total_clauses} clauses across ~${tree.total_pages} pages.`
        );
        setCachedTree(tree.structure);
        saveToSettings("miai_tree", tree.structure);
        renderClauseTree(tree.structure);
        setStatus("Ready", "ready");
        setDocumentReady(true);
        checkAutoSave();
        loadChatHistory();
        return;
      }

      setStatus(`Analysing… (${elapsed}s)`, "loading");
      if (i % 6 === 0) {
        addMessage("system", `Still analysing your document… (${elapsed}s)`);
      }
    } catch (pollErr) {
      // devLog("POLL_ERR", pollErr.message);
    }
  }

  addMessage("system", "This is taking longer than expected. Please try reopening the document.");
  setStatus("Timed out", "error");
}
